import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, TextField, Button, Typography, Alert, CircularProgress,
  Select, MenuItem, FormControl, InputLabel, InputAdornment,
  Avatar, Paper, LinearProgress, Stepper, Step, StepLabel,
  Grid, Divider
} from '@mui/material';
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  CheckCircle as CheckIcon,
  ArrowForward as ArrowIcon,
  ArrowBack as BackIcon,
  CameraAlt as CameraIcon,
  HourglassEmpty as HourglassIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../context/AuthContext';
import { awsUserService } from '../../services/aws-user-service';

const steps = ['Basic Info', 'Personal Details', 'Address & Financials', 'Live Photo'];

const GENDERS = ['Male', 'Female', 'Other'];
const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const MARITAL_STATUS = ['Single', 'Married', 'Divorced', 'Widowed'];

const validationSchemas = [
  Yup.object({
    firstName: Yup.string().required('Required'),
    lastName: Yup.string().required('Required'),
    phone: Yup.string().required('Required').matches(/^\d{10}$/, 'Must be 10 digits'),
    nickName: Yup.string(),
    aboutMe: Yup.string(),
  }),
  Yup.object({
    gender: Yup.string().required('Required'),
    bloodGroup: Yup.string().required('Required'),
    maritalStatus: Yup.string().required('Required'),
    officialDob: Yup.date().required('Required'),
    actualDob: Yup.date().required('Required'),
    aadhaarNo: Yup.string().required('Required').matches(/^\d{12}$/, 'Must be 12 digits'),
    nominee: Yup.string().required('Required'),
  }),
  Yup.object({
    presentAddress: Yup.string().required('Required'),
    permanentAddress: Yup.string().required('Required'),
    pfAccountNumber: Yup.string(),
    uan: Yup.string(),
    salaryAccountNumber: Yup.string(),
    ifscCode: Yup.string().matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Invalid IFSC format'),
    panNumber: Yup.string().matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, 'Invalid PAN format'),
  })
];

const StaffOnboarding: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, updateUserProfile, userProfile } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Camera state
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [photoData, setPhotoData] = useState<string | null>(null);

  // If already onboarding completed, check if pending HR approval
  useEffect(() => {
    if (userProfile?.staffOnboardingCompleted) {
      if (userProfile?.hrDetails?.onboarding_status === 'pending_hr_approval') {
        // Show waiting room (handled in render)
      } else if (userProfile?.hrDetails?.onboarding_status === 'active') {
        navigate('/dashboard');
      }
    }
  }, [userProfile, navigate]);

  // Handle camera lifecycle
  useEffect(() => {
    if (activeStep === 3 && !photoData) {
      startCamera();
    } else {
      stopCamera();
    }
    return () => stopCamera();
  }, [activeStep, photoData]);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ video: true });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError('Could not access camera. Please allow camera permissions.');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const context = canvasRef.current.getContext('2d');
      if (context) {
        canvasRef.current.width = videoRef.current.videoWidth;
        canvasRef.current.height = videoRef.current.videoHeight;
        context.drawImage(videoRef.current, 0, 0);
        const dataUrl = canvasRef.current.toDataURL('image/jpeg', 0.8);
        setPhotoData(dataUrl);
        stopCamera();
      }
    }
  };

  const retakePhoto = () => {
    setPhotoData(null);
  };

  const formik = useFormik({
    initialValues: {
      // Step 0
      firstName: '', lastName: '', phone: '', nickName: '', aboutMe: '',
      // Step 1
      gender: '', bloodGroup: '', maritalStatus: '', officialDob: '', actualDob: '', aadhaarNo: '', nominee: '',
      // Step 2
      presentAddress: '', permanentAddress: '', pfAccountNumber: '', uan: '', salaryAccountNumber: '', ifscCode: '', panNumber: '',
    },
    validationSchema: activeStep < 3 ? validationSchemas[activeStep] : undefined,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: async () => {
      // Handled manually by handleNext
    },
  });

  const handleNext = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      formik.setTouched(Object.keys(errors).reduce((acc, curr) => ({ ...acc, [curr]: true }), {}));
      return;
    }
    
    if (activeStep === 3) {
      if (!photoData) {
        setError('Please capture your profile photo to continue.');
        return;
      }
      submitProfile();
    } else {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    setError(null);
  };

  const submitProfile = async () => {
    if (!currentUser) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const displayName = `${formik.values.firstName} ${formik.values.lastName}`.trim();

      const profileUpdate = {
        displayName,
        firstName: formik.values.firstName,
        lastName: formik.values.lastName,
        phone: formik.values.phone,
        staffOnboardingCompleted: true,
        hrDetails: {
          ...userProfile?.hrDetails, // Preserve existing
          onboarding_status: 'pending_hr_approval',
          photo_url: photoData, // Base64 stored for simplicity in V1
          nick_name: formik.values.nickName,
          about_me: formik.values.aboutMe,
          gender: formik.values.gender,
          blood_group: formik.values.bloodGroup,
          marital_status: formik.values.maritalStatus,
          official_dob: formik.values.officialDob,
          actual_dob: formik.values.actualDob,
          aadhaar_no: formik.values.aadhaarNo,
          nominee: formik.values.nominee,
          present_address: formik.values.presentAddress,
          permanent_address: formik.values.permanentAddress,
          pf_account_number: formik.values.pfAccountNumber,
          uan: formik.values.uan,
          salary_account_number: formik.values.salaryAccountNumber,
          ifsc_code: formik.values.ifscCode,
          pan_number: formik.values.panNumber,
        }
      };

      await awsUserService.updateUserProfile(currentUser.uid, profileUpdate);
      await updateUserProfile(profileUpdate);
      
    } catch (err: any) {
      console.error('Staff onboarding error:', err);
      setError(err.message || 'Failed to save profile. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (userProfile?.hrDetails?.onboarding_status === 'pending_hr_approval') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a' }}>
        <Box sx={{ textAlign: 'center', color: 'white', maxWidth: 400, p: 4 }}>
          <HourglassIcon sx={{ fontSize: 72, color: '#f59e0b', mb: 2, animation: 'spin 4s linear infinite' }} />
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Waiting for HR Approval
          </Typography>
          <Typography color="grey.400" mt={2}>
            Your profile has been submitted successfully. Please wait while your HR administrator reviews and approves your account, and grants you access to the system modules.
          </Typography>
          <Button variant="outlined" color="inherit" onClick={() => window.location.reload()} sx={{ mt: 4 }}>
            Refresh Status
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a', p: 2 }}>
      <Box sx={{ width: '100%', maxWidth: 700 }}>
        
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Avatar sx={{ width: 56, height: 56, mx: 'auto', mb: 2, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>
            👋
          </Avatar>
          <Typography variant="h4" fontWeight={800} color="white">
            Employee Profile Setup
          </Typography>
          <Typography color="grey.400">
            Please fill in your authentic details. HR will verify these before granting access.
          </Typography>
        </Box>

        <Paper elevation={0} sx={{ p: 4, borderRadius: 4, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
          
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4, '& .MuiStepLabel-label': { color: 'grey.500' }, '& .Mui-active': { color: 'white !important' }, '& .Mui-completed': { color: '#818cf8 !important' } }}>
            {steps.map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          <Box sx={{ minHeight: 300 }}>
            {activeStep === 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="First Name" name="firstName" value={formik.values.firstName} onChange={formik.handleChange} error={formik.touched.firstName && Boolean(formik.errors.firstName)} helperText={formik.touched.firstName && formik.errors.firstName} sx={inputStyles} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Last Name" name="lastName" value={formik.values.lastName} onChange={formik.handleChange} error={formik.touched.lastName && Boolean(formik.errors.lastName)} helperText={formik.touched.lastName && formik.errors.lastName} sx={inputStyles} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Phone Number" name="phone" placeholder="10-digit number" value={formik.values.phone} onChange={formik.handleChange} error={formik.touched.phone && Boolean(formik.errors.phone)} helperText={formik.touched.phone && formik.errors.phone} sx={inputStyles} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Nick Name (Optional)" name="nickName" value={formik.values.nickName} onChange={formik.handleChange} sx={inputStyles} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline rows={3} label="About Me (Optional)" name="aboutMe" value={formik.values.aboutMe} onChange={formik.handleChange} sx={inputStyles} />
                </Grid>
              </Grid>
            )}

            {activeStep === 1 && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth sx={inputStyles}>
                    <InputLabel>Gender</InputLabel>
                    <Select name="gender" value={formik.values.gender} onChange={formik.handleChange}>
                      {GENDERS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth sx={inputStyles}>
                    <InputLabel>Blood Group</InputLabel>
                    <Select name="bloodGroup" value={formik.values.bloodGroup} onChange={formik.handleChange}>
                      {BLOOD_GROUPS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth sx={inputStyles}>
                    <InputLabel>Marital Status</InputLabel>
                    <Select name="maritalStatus" value={formik.values.maritalStatus} onChange={formik.handleChange}>
                      {MARITAL_STATUS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth type="date" label="Official DOB (as per Aadhar)" name="officialDob" value={formik.values.officialDob} onChange={formik.handleChange} error={formik.touched.officialDob && Boolean(formik.errors.officialDob)} helperText={formik.touched.officialDob && formik.errors.officialDob} InputLabelProps={{ shrink: true }} sx={inputStyles} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth type="date" label="Actual DOB" name="actualDob" value={formik.values.actualDob} onChange={formik.handleChange} error={formik.touched.actualDob && Boolean(formik.errors.actualDob)} helperText={formik.touched.actualDob && formik.errors.actualDob} InputLabelProps={{ shrink: true }} sx={inputStyles} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Aadhaar No" name="aadhaarNo" value={formik.values.aadhaarNo} onChange={formik.handleChange} error={formik.touched.aadhaarNo && Boolean(formik.errors.aadhaarNo)} helperText={formik.touched.aadhaarNo && formik.errors.aadhaarNo} sx={inputStyles} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Nominee Name" name="nominee" value={formik.values.nominee} onChange={formik.handleChange} error={formik.touched.nominee && Boolean(formik.errors.nominee)} helperText={formik.touched.nominee && formik.errors.nominee} sx={inputStyles} />
                </Grid>
              </Grid>
            )}

            {activeStep === 2 && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography color="white" variant="subtitle2" mb={1}>Address</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth multiline rows={2} label="Present Address" name="presentAddress" value={formik.values.presentAddress} onChange={formik.handleChange} error={formik.touched.presentAddress && Boolean(formik.errors.presentAddress)} helperText={formik.touched.presentAddress && formik.errors.presentAddress} sx={inputStyles} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth multiline rows={2} label="Permanent Address" name="permanentAddress" value={formik.values.permanentAddress} onChange={formik.handleChange} error={formik.touched.permanentAddress && Boolean(formik.errors.permanentAddress)} helperText={formik.touched.permanentAddress && formik.errors.permanentAddress} sx={inputStyles} />
                </Grid>
                
                <Grid item xs={12}><Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} /></Grid>
                <Grid item xs={12}>
                  <Typography color="white" variant="subtitle2" mb={1}>Financials</Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="PF Account Number (Optional)" name="pfAccountNumber" value={formik.values.pfAccountNumber} onChange={formik.handleChange} sx={inputStyles} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="UAN (Optional)" name="uan" value={formik.values.uan} onChange={formik.handleChange} sx={inputStyles} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Salary A/C No" name="salaryAccountNumber" value={formik.values.salaryAccountNumber} onChange={formik.handleChange} sx={inputStyles} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="IFSC Code" name="ifscCode" value={formik.values.ifscCode} onChange={formik.handleChange} sx={inputStyles} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="PAN Card" name="panNumber" value={formik.values.panNumber} onChange={formik.handleChange} sx={inputStyles} />
                </Grid>
              </Grid>
            )}

            {activeStep === 3 && (
              <Box textAlign="center">
                <Typography color="white" mb={3}>Please capture a clear photo of your face.</Typography>
                
                <Box sx={{ width: 320, height: 240, mx: 'auto', bgcolor: 'black', borderRadius: 4, overflow: 'hidden', position: 'relative', border: '2px solid rgba(255,255,255,0.1)' }}>
                  {!photoData ? (
                    <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <img src={photoData} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  )}
                  <canvas ref={canvasRef} style={{ display: 'none' }} />
                </Box>

                <Box mt={4}>
                  {!photoData ? (
                    <Button variant="contained" onClick={capturePhoto} startIcon={<CameraIcon />} sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' } }}>
                      Capture Photo
                    </Button>
                  ) : (
                    <Button variant="outlined" onClick={retakePhoto} sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}>
                      Retake Photo
                    </Button>
                  )}
                </Box>
              </Box>
            )}
          </Box>

          {/* Navigation */}
          <Box display="flex" justifyContent="space-between" mt={4} pt={2} borderTop="1px solid rgba(255,255,255,0.1)">
            <Button
              onClick={handleBack}
              disabled={activeStep === 0 || isSubmitting}
              startIcon={<BackIcon />}
              sx={{ color: 'grey.400' }}
            >
              Back
            </Button>
            <Button
              onClick={handleNext}
              disabled={isSubmitting}
              variant="contained"
              endIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <ArrowIcon />}
              sx={{
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' }
              }}
            >
              {activeStep === steps.length - 1 ? 'Submit Profile' : 'Next'}
            </Button>
          </Box>

        </Paper>
      </Box>
    </Box>
  );
};

const inputStyles = {
  '& .MuiOutlinedInput-root': {
    color: 'white',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
    '&.Mui-focused fieldset': { borderColor: '#6366f1' },
  },
  '& .MuiInputLabel-root': { color: 'grey.500' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#818cf8' },
  '& .MuiFormHelperText-root': { color: '#f87171' },
};

export default StaffOnboarding;
